import { useMemo } from 'react';
import { IVehicleTile } from '../../../types/vehicleTile';
import useAppState from '../../../common/state/appState';

const TARGET_VIEWED_VEHICLE_COUNT = 10;
const TARGET_RECOMMENDED_VEHICLE_COUNT = 10;

interface IUftVehicleDisplaySplit {
    displayedViewedVehicles: IVehicleTile[];
    remainingRecommendedVehicles: IVehicleTile[];
    addedFromRecommendedVehiclesCount: number;
}

export const splitUftVehicleDisplay = (
    recentlyViewedVehicles: IVehicleTile[],
    uftRecommendedVehicles: IVehicleTile[],
    shouldTopUpFromRecommendations = true,
): IUftVehicleDisplaySplit => {
    const viewedVehiclesForDisplay = recentlyViewedVehicles.slice(0, TARGET_VIEWED_VEHICLE_COUNT);

    if (viewedVehiclesForDisplay.length === 0 || !shouldTopUpFromRecommendations) {
        return {
            displayedViewedVehicles: viewedVehiclesForDisplay,
            remainingRecommendedVehicles: uftRecommendedVehicles.slice(0, TARGET_RECOMMENDED_VEHICLE_COUNT),
            addedFromRecommendedVehiclesCount: 0,
        };
    }

    const usedStockNumbers = new Set(viewedVehiclesForDisplay.map((v) => v.stockNumber));
    const topUpVehicles: IVehicleTile[] = [];

    for (const vehicle of uftRecommendedVehicles) {
        if (viewedVehiclesForDisplay.length + topUpVehicles.length >= TARGET_VIEWED_VEHICLE_COUNT) {
            break;
        }

        if (!usedStockNumbers.has(vehicle.stockNumber)) {
            topUpVehicles.push(vehicle);
            usedStockNumbers.add(vehicle.stockNumber);
        }
    }

    return {
        displayedViewedVehicles: [...viewedVehiclesForDisplay, ...topUpVehicles],
        remainingRecommendedVehicles: uftRecommendedVehicles
            .filter((v) => !usedStockNumbers.has(v.stockNumber))
            .slice(0, TARGET_RECOMMENDED_VEHICLE_COUNT),
        addedFromRecommendedVehiclesCount: topUpVehicles.length,
    };
};

export const useUftVehicleDisplay = (): IUftVehicleDisplaySplit & { shouldTopUpFromRecommendations: boolean } => {
    const recentlyViewedVehicles = useAppState((state) => state.recentlyViewedVehicles);
    const uftRecommendedVehicles = useAppState((state) => state.uftRecommendedVehicles);
    const optimizedReturnExperience = useAppState((state) => state.optimizedReturnExperience);
    const shouldTopUpFromRecommendations = optimizedReturnExperience === 'test';

    const split = useMemo(
        () => splitUftVehicleDisplay(recentlyViewedVehicles, uftRecommendedVehicles, shouldTopUpFromRecommendations),
        [recentlyViewedVehicles, uftRecommendedVehicles, shouldTopUpFromRecommendations],
    );

    return { ...split, shouldTopUpFromRecommendations };
};
